/**
 * SongDetailsWidget Component
 * Displays currently playing song details in dashboard sidebars
 * "Now Playing" widget showing audio player state
 */

import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { Card } from '@/components/ui/Card';
import {
  Music,
  Clock,
  Play,
  Pause,
  Volume2
} from 'lucide-react';

/**
 * Format seconds to m:ss format
 */
function formatDuration(seconds?: number): string {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function SongDetailsWidget() {
  const { currentTrack, isPlaying, currentTime, duration } = useAudioPlayer();

  if (!currentTrack) {
    return (
      <Card variant="bordered" padding="sm">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Now Playing</h3>
        <div className="text-center py-4">
          <Music className="w-6 h-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500 dark:text-gray-400">No song playing</p>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="bordered" padding="sm">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Now Playing</h3>

      {/* Song Title and Artist */}
      <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">Title</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{currentTrack.title}</p>

        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mt-1 truncate">Artist</p>
        <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{currentTrack.artist}</p>
      </div>

      {/* Duration - using duration_seconds from Song type */}
      {currentTrack.duration && (
        <div className="flex items-center space-x-1.5 mb-1.5 text-xs">
          <Clock className="w-3 h-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-400">Duration:</span>
          <span className="font-medium text-gray-900 dark:text-white">{formatDuration(currentTrack.duration)}</span>
        </div>
      )}

      {/* Playback Position */}
      {duration > 0 && (
        <div className="flex items-center space-x-1.5 mb-1.5 text-xs">
          <Volume2 className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-gray-600 dark:text-gray-400">Position:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </span>
        </div>
      )}

      {/* Playback Status */}
      <div className="flex items-center space-x-1.5 mb-1.5 text-xs">
        {isPlaying ? (
          <Play className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0" />
        ) : (
          <Pause className="w-3 h-3 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
        )}
        <span className="text-gray-600 dark:text-gray-400">Status:</span>
        <span className={`font-medium ${isPlaying ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
          {isPlaying ? 'Playing' : 'Paused'}
        </span>
      </div>
    </Card>
  );
}